import { filterTypes, storageItemName } from './config';
import { getDebugMode } from './configStore';
import config from './config.json';
import isEqual from 'lodash.isequal';
import * as storage from './browserStorage';

/**
 * Converts string boolean values to actual booleans.
 * Converts string number values to actual numbers.
 *
 * @param {object} atts Shortcode attributes.
 * @returns {object}
 */
export const shortcodeAttsHelper = (atts) =>
    Object.fromEntries(
        Object.entries(atts).map(([att, value]) => {
            if (value === 'true') {
                return [att, true];
            } else if (value === 'false') {
                return [att, false];
            } else if (typeof value === 'string' && !isNaN(value)) {
                return [att, Number(value)];
            } else {
                return [att, value];
            }
        })
    );

/**
 * Remove excess whitespace and special characters, convert to lowercase and replace spaces with dashes.
 * "This is a String!" -> "this-is-a-string"
 *
 * @param {string} str
 * @returns {string}
 */
export const toLowerWithDashes = (str) =>
    str
        .replace("'", '')
        .replace(/[\W_\s]+/g, ' ')
        .trim()
        .replaceAll(' ', '-')
        .toLowerCase();

/**
 * Gets existing checkout info from localStorage, if it exists.
 * @returns {object|false}
 */
export const getExistingCheckout = () => {
    const checkout = storage.getItem(storageItemName);

    if (typeof checkout === 'string' && checkout !== 'undefined') {
        return JSON.parse(checkout);
    } else {
        return false;
    }
};

/**
 * Get initial menu filter object.
 *
 * @param {object} params useParams
 * @param {object} filterTerms Object containing arrays of filter terms.
 * @param {object} menuFilter Default filter from globalConfig.
 *
 * @returns {object}
 */
export const getInitialMenuFilter = (
    params,
    filterTerms,
    menuFilter,
    searchQuery,
    thcMin
) => {
    const filters = Object.assign({}, menuFilter);

    if (params?.categoryType) {
        switch (params?.categoryType.toLowerCase()) {
            case 'special':
                if (params?.categoryName) {
                    filters.specials = true;
                }
                break;
            case 'brand':
                if (params?.categoryName) {
                    const brandId = brandIdLookup(
                        params.categoryName,
                        filterTerms.brands
                    );

                    if (brandId) {
                        filters.brandIds = [brandId];
                    }
                }
                break;
            case 'specials':
                filters.specials = true;

                if (
                    params?.categoryName?.toLowerCase() === 'brand' &&
                    params?.specialCategoryName
                ) {
                    const brandId = brandIdLookup(
                        params.specialCategoryName,
                        filterTerms.brands
                    );

                    if (brandId) {
                        filters.brandIds = [brandId];
                    }
                }

                if (
                    params?.categoryName?.toLowerCase() === 'category' &&
                    params?.specialCategoryName
                ) {
                    filters.category = params.specialCategoryName.toUpperCase();
                }
                break;
            case 'tags':
                filters.tags = [params?.categoryName];
                break;
            case 'subcategory':
                filters.subcategory = params?.categoryName.toUpperCase();
                break;
            default:
                if (
                    params?.specialCategoryName &&
                    params?.categoryName?.toLowerCase() === 'category'
                ) {
                    const [filterType, filterName] = verifiedParams(
                        params.categoryName,
                        params.specialCategoryName,
                        filterTerms
                    );

                    if (filterType && filterName) {
                        filters[filterType] = filterName;
                    }
                } else if (params?.categoryName) {
                    const [filterType, filterName] = verifiedParams(
                        params.categoryType,
                        params.categoryName,
                        filterTerms
                    );

                    if (filterType && filterName) {
                        filters[filterType] = filterName;
                    }
                }
                break;
        }
    }

    if (searchQuery) {
        filters.search = searchQuery;
    }

    if (thcMin) {
        filters.potencyThc = {
            ...filters.potencyThc,
            min: parseFloat(thcMin)
        };
    }

    return filters;
};

/**
 * Remove empty `menuFilter` items, as well as specials property.
 * Used before sending in a GQL query.
 *
 * @param {object} providedMenuFilter
 * @param {object} menuFilter Default menu filter from global config.
 * @param {object|null} tagSlugs Tag slugs for current retailer.
 *
 * @returns {object}
 */
export const cleanMenuFilterForQuery = (
    providedMenuFilter,
    menuFilter,
    tagSlugs = null
) => {
    const clone = Object.assign({}, providedMenuFilter);
    delete clone.specials;

    function isNotEmptyValue(v) {
        return (typeof v === 'string' || Array.isArray(v)) && v.length > 0;
    }

    function isNotDefaultPotency(k, v) {
        // First check if it's a potency field
        if (k !== 'potencyThc' && k !== 'potencyCbd') {
            return false;
        }

        // If v is null, undefined, or doesn't exist, it's default
        if (!v || typeof v !== 'object') {
            return false;
        }

        // Check if the potency has meaningful values
        // Consider it default if min is 0/null/undefined AND max is null/undefined
        const hasMinValue = v.min !== undefined && v.min !== null && v.min !== 0;
        const hasMaxValue = v.max !== undefined && v.max !== null;

        // If neither min nor max have meaningful values, it's default
        if (!hasMinValue && !hasMaxValue) {
            return false;
        }

        // Also check against the default menuFilter values
        return !isEqual(v, menuFilter[k]);
    }

    if (tagSlugs && Array.isArray(clone.tags) && clone.tags.length > 0) {
        clone.tags = clone.tags.map((tag) =>
            tagSlugs.hasOwnProperty(tag) ? tagSlugs[tag] : tag
        );
    }

    // only returns keys that are strings or arrays with one or more values, or non-default potency.
    const cleaned = Object.entries(clone).filter(([k, v]) => {
        return isNotEmptyValue(v) || isNotDefaultPotency(k, v);
    });

    return Object.fromEntries(cleaned);
};

/**
 * Checks if query is only filtered for brands.
 * @param {object} providedMenuFilter Current menu filter.
 * @param {object} menuFilter Default menu filter from global config.
 * @returns {bool}
 */
export const isOnlyBrandQuery = (providedMenuFilter, menuFilter) => {
    const cleanMenu = cleanMenuFilterForQuery(providedMenuFilter, menuFilter);
    const keys = Object.keys(cleanMenu);

    return keys.length === 1 && keys[0] === filterTypes.brands;
};

/**
 * Checks if query has no filters.
 * @param {object} providedMenuFilter Current menu filter.
 * @param {object} menuFilter Default menu filter from global config.
 * @returns {bool}
 */
export const isEmptyFilter = (providedMenuFilter, menuFilter) => {
    const cleanMenu = cleanMenuFilterForQuery(providedMenuFilter, menuFilter);
    const keys = Object.keys(cleanMenu);

    return keys.length === 0;
};

/**
 * If no retailer data from shortcode, get stored id or return false to trigger modal.
 * Otherwise, return the provided id. If error, return stored id, then false to trigger modal.
 *
 * @param {object} retailer Retailer shortcode data.
 * @param {object} params useParams params.
 * @param {object} retailers Retailer config settings.
 *
 * @returns {string|false}
 */
export const getRetailerId = (retailerShortcode, params, retailers) => {
    if (retailerShortcode) {
        return retailerShortcode;
    }

    if (params?.viewName === 'shop') {
        if (params?.locationSlug) {
            const retailerMatch = Object.values(retailers).find(
                (retailer) => retailer.menuSlug === params.locationSlug
            );

            if (retailerMatch) {
                return retailerMatch.id;
            } else {
                return false;
            }
        } else {
            return getExistingCheckout().retailerId || false;
        }
    } else if (!retailerShortcode) {
        return getExistingCheckout().retailerId || false;
    }

    return (
        retailerShortcode?.providedId ||
        getExistingCheckout().retailerId ||
        false
    );
};

/**
 * Get brand ID from URL param brand-name.
 *
 * @param {string} brandName :catergoryType param
 * @param {array} brandTerms filterTerms.brands
 * @param {bool} returnObj return entire brand object, or just ID. default false (just id).
 *
 * @returns {string|false}
 */
export const brandIdLookup = (brandName, brandTerms, returnObj = false) => {
    if (!brandTerms) {
        return false;
    }

    const brandWords = brandName.split('-');

    const brand = brandTerms.find((term) => {
        let termWords = toLowerWithDashes(term.name).split('-');
        return brandWords.every((word) => termWords.includes(word));
    });

    if (brand && !returnObj) {
        return brand.id;
    } else if (brand && returnObj) {
        return brand;
    } else {
        return false;
    }
};

/**
 * Checks for valid category/strain/effect params and returns values to use in filter.
 *
 * @param {string} type params.categoryType
 * @param {string} name params.categoryName
 * @param {object} filterTerms filterTerms for this retailer
 *
 * @returns {array}
 */
export const verifiedParams = (type, name, filterTerms) => {
    /**
     * key is what is expected from :categoryType param.
     */
    const validTypes = {
        category: {
            filterTerm: 'categories',
            filterType: 'category',
        },
        strain: { filterTerm: 'strains', filterType: 'strainType' },
        effect: { filterTerm: 'effects', filterType: 'effects' },
        specials: { filterTerm: 'categories', filterType: 'category' },
    };

    const isValidType = validTypes.hasOwnProperty(type.toLowerCase());

    if (isValidType) {
        const isValidName = filterTerms[
            validTypes[type.toLowerCase()].filterTerm
        ].includes(name.toUpperCase().replace('-', '_'));

        if (isValidName) {
            const filterType = validTypes[type.toLowerCase()].filterType;
            return [
                filterType,
                filterType === 'effects'
                    ? [name.toUpperCase().replace('-', '_')]
                    : name.toUpperCase().replace('-', '_'),
            ];
        }
    }

    return [false, false];
};

/**
 * Convert a string or an array of strings to uppercase words like in PHP ucwords()
 * @param {string|string[]} strOrArray
 * @returns {string|string[]}
 */
export const ucwords = (strOrArray) => {
    if (typeof strOrArray === 'string') {
        return (
            strOrArray.charAt(0).toUpperCase() +
            strOrArray.slice(1).toLowerCase()
        );
    } else if (Array.isArray(strOrArray)) {
        return strOrArray.map(function (str) {
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        });
    }
    return strOrArray;
};

/**
 * Get today's day e.g. 'Friday'.
 * @returns string
 */
export const today = () => {
    const days = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ];

    return days[new Date().getDay()];
};

/**
 * Determines whether store is open or closed.
 * @see https://stackoverflow.com/a/26078713
 * @param {object} todaysHours retailerData hours object.
 * @returns {bool}
 */
export const isOpen = (todaysHours) => {
    if (!todaysHours.active) {
        return false;
    }

    let { start, end } = todaysHours;

    function timeToDecimal(str) {
        let parts = str.split(':');

        if (parts.length <= 1) {
            return false;
        }

        let dec = parts[1].split(' ');

        const hasPM = dec[1] && dec[1].includes('PM');
        const hasAM = dec[1] && dec[1].includes('AM');
        let hour = parseInt(parts[0], 10);

        // 12:XX AM is midnight (0), not noon (12)
        if (hasAM && hour === 12) hour = 0;
        // 1:XX PM – 11:XX PM becomes 13 – 23
        if (hasPM && hour !== 12) hour += 12;

        const minFrac = parseInt((parseInt(dec[0], 10) / 6) * 10, 10);

        let float = parseFloat(
            hour + '.' + (minFrac < 10 ? '0' : '') + minFrac
        );

        // Times flagged "(Next Day)" belong to tomorrow's timeline
        if (/\(Next Day\)/i.test(str)) float += 24;

        return float;
    }

    start = timeToDecimal(start);
    end = timeToDecimal(end);

    let now = timeToDecimal(
        new Date().toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        })
    );

    // Overnight hours: if close time is past midnight and we're in the
    // early-morning window before opening, shift `now` onto the same timeline.
    if (end > 24 && now < start) {
        now += 24;
    }

    return now < end && now > start;
};

/**
 * Scroll to the shop container.
 * @returns {void}
 */
export const scrollToShop = (customId = null, fixedId = null) => {
    const elementId = customId !== null ? customId : 'shop';
    const shopElement = document.getElementById(elementId);

    if (shopElement) {
        if (fixedId) {
            const fixedElement = document.getElementById(fixedId);
            const topFixedPosition = shopElement.offsetTop - 150;
            fixedElement.scrollTo({
                top: topFixedPosition,
                behavior: 'smooth',
            });
        } else {
            const topPosition =
                shopElement.getBoundingClientRect().top +
                window.pageYOffset -
                150;
            window.scrollTo({ top: topPosition, behavior: 'smooth' });
        }
    }
};

/**
 * Hide the menu banner.
 * Used if navigating or filtering to a category - no need to display the menu banner that takes up real estate.
 * @returns {void}
 */
export const hideMenuBanner = () => {
    const banner = document.getElementById('headlessMenuBanners');

    if (banner) {
        banner.style.display = 'none';
    }
};

/**
 * Reformat know categories for better reading in HTML output.
 * "PRE_ROLLS" -> "Pre-Rolls"
 *
 * @param {string} str
 * @returns {string}
 */
export const formatCategory = (str) => {
    const lookup = {
        ACCESSORIES: 'Accessories',
        APPAREL: 'Apparel',
        CBD: 'CBD',
        CONCENTRATES: 'Concentrates',
        EDIBLES: 'Edibles',
        FLOWER: 'Flower',
        ORALS: 'Orals',
        PRE_ROLLS: 'Pre-Rolls',
        SEEDS: 'Seeds',
        TINCTURES: 'Tinctures',
        TOPICALS: 'Topicals',
        VAPORIZERS: 'Vaporizers',
        staff_picks: 'Staff Picks',
    };

    return lookup[str] || ucwords(str);
};

/**
 * SEO-friendly tags.
 *
 * @param {object} selectedFilters
 * @param {object} filterTerms
 *
 * @returns {string}
 */
export const getOrderText = (selectedFilters, filterTerms) => {
    let selectedBrandName = '';
    let orderText = 'Shop & Order';

    if (
        selectedFilters &&
        selectedFilters[filterTypes.brands] &&
        Array.isArray(selectedFilters[filterTypes.brands]) &&
        selectedFilters[filterTypes.brands].length > 0
    ) {
        const selectedBrandId = selectedFilters[filterTypes.brands][0];
        if (
            filterTerms &&
            filterTerms.brands &&
            Array.isArray(filterTerms.brands)
        ) {
            const selectedBrand = filterTerms.brands.find(
                (brand) => brand && brand.id === selectedBrandId
            );
            if (selectedBrand && selectedBrand.name) {
                selectedBrandName = selectedBrand.name;
            }
        }
    }

    if (selectedFilters && selectedFilters[filterTypes.category]) {
        orderText += ` ${formatCategory(
            selectedFilters[filterTypes.category]
        )}`;
    }

    orderText += ' Online';

    if (selectedBrandName) {
        orderText = `${selectedBrandName} | ${orderText}`;
    }

    return orderText;
};

/**
 * Check for stale session and reload.
 * @return {bool}
 */
export function checkStaleSession() {
    const oneDay = 60 * 60 * 24 * 1000;
    const existingCheckout = getExistingCheckout();

    if (existingCheckout && existingCheckout.createdAt) {
        const { retailerId, checkoutId, createdAt } = existingCheckout;
        if (Date.now() - createdAt > oneDay) {
            storage.setItem(
                storageItemName,
                JSON.stringify({
                    checkoutId,
                    retailerId,
                    createdAt: Date.now(),
                })
            );
            location.reload();
            return true;
        }
    }

    return false;
}

/**
 * Parse an embed script tag.
 * Used for reconstructing embed in fallback events.
 *
 * @param {string} scriptString
 * @returns {object}
 */
export const parseScriptTag = (scriptString) => {
    const scriptRegex = /<script\s+([\s\S]*?)><\/script>/i;
    const attributeRegex =
        /(\w+)=["']?((?:.(?!["']?\s+(?:\S+)=|[>"']))+.)["']?/g;

    const scriptMatch = scriptString.match(scriptRegex);

    if (scriptMatch && scriptMatch[1]) {
        const attributeString = scriptMatch[1];
        const scriptAttributes = {};

        let attributeMatch;
        while ((attributeMatch = attributeRegex.exec(attributeString))) {
            const attrName = attributeMatch[1];
            const attrValue = attributeMatch[2];
            scriptAttributes[attrName] = attrValue;
        }

        return scriptAttributes;
    }

    return null;
};

/**
 * Manually filters response by prices.
 * @param {Array} resProducts Products from query response.
 * @param {object} priceFilters Min and max prices.
 * @param {string} pricingType Rec or med.
 * @param {Number} paginationLimit Pagination limit.
 *
 * @returns {Array} Filtered products.
 */
export function getPriceFilteredProducts(
    resProducts,
    priceFilters,
    pricingType,
    paginationLimit
) {
    let priceFilteredProducts = [];

    let priceMin;
    let priceMax;

    if (priceFilters.hasOwnProperty('min')) {
        priceMin = priceFilters.min;
        priceMax = priceFilters.max;
    } else {
        priceMin = priceFilters.priceMin;
        priceMax = priceFilters.priceMax;
    }

    if (priceMin && priceMax) {
        priceFilteredProducts = resProducts.filter((product, i) => {
            if (i >= paginationLimit) return false;

            if (pricingType === 'RECREATIONAL') {
                return (
                    Math.round(product.variants[0].priceRec) > priceMin &&
                    Math.round(product.variants[0].priceRec) < priceMax
                );
            } else if (pricingType === 'MEDICINAL') {
                return (
                    Math.round(product.variants[0].priceMed) > priceMin &&
                    Math.round(product.variants[0].priceMed) < priceMax
                );
            }
        });
    } else if (priceMin) {
        priceFilteredProducts = resProducts.filter((product, i) => {
            if (i >= paginationLimit) return false;

            if (pricingType === 'RECREATIONAL') {
                return Math.round(product.variants[0].priceRec) > priceMin;
            } else if (pricingType === 'MEDICINAL') {
                return Math.round(product.variants[0].priceMed) > priceMin;
            }
        });
    } else if (priceMax) {
        priceFilteredProducts = resProducts.filter((product, i) => {
            if (i >= paginationLimit) return false;

            if (pricingType === 'RECREATIONAL') {
                return Math.round(product.variants[0].priceRec) < priceMax;
            } else if (pricingType === 'MEDICINAL') {
                return Math.round(product.variants[0].priceMed) < priceMax;
            }
        });
    }

    return priceFilteredProducts;
}

/**
 * Convert 2-letter state abbreviations to the full state name.
 * Used for the location gate.
 *
 * @param {string} stateName
 * @returns {string}
 */
export const convertStateCode = (stateName) => {
    const stateCodes = {
        AL: 'Alabama',
        AK: 'Alaska',
        AZ: 'Arizona',
        AR: 'Arkansas',
        CA: 'California',
        CO: 'Colorado',
        CT: 'Connecticut',
        DE: 'Delaware',
        FL: 'Florida',
        GA: 'Georgia',
        HI: 'Hawaii',
        ID: 'Idaho',
        IL: 'Illinois',
        IN: 'Indiana',
        IA: 'Iowa',
        KS: 'Kansas',
        KY: 'Kentucky',
        LA: 'Louisiana',
        ME: 'Maine',
        MD: 'Maryland',
        MA: 'Massachusetts',
        MI: 'Michigan',
        MN: 'Minnesota',
        MS: 'Mississippi',
        MO: 'Missouri',
        MT: 'Montana',
        NE: 'Nebraska',
        NV: 'Nevada',
        NH: 'New Hampshire',
        NJ: 'New Jersey',
        NM: 'New Mexico',
        NY: 'New York',
        NC: 'North Carolina',
        ND: 'North Dakota',
        OH: 'Ohio',
        OK: 'Oklahoma',
        OR: 'Oregon',
        PA: 'Pennsylvania',
        RI: 'Rhode Island',
        SC: 'South Carolina',
        SD: 'South Dakota',
        TN: 'Tennessee',
        TX: 'Texas',
        UT: 'Utah',
        VT: 'Vermont',
        VA: 'Virginia',
        WA: 'Washington',
        WV: 'West Virginia',
        WI: 'Wisconsin',
        WY: 'Wyoming',
        DC: 'District of Columbia',
    };

    return stateCodes[stateName.toUpperCase()] || stateName;
};

/**
 * Open provided URL in new tab, or current url if not provided.
 *
 * @param {string|null} url Optional URL.
 * @returns void
 */
export function openLinkInNewTab(url = null) {
    window.open(url || window.location.href, '_blank');
}

/**
 * Remove trailing slash, if it exists.
 *
 * @param {string} url
 * @returns string
 */
export function removeTrailingSlash(url) {
    return url.replace(/\/$/, '');
}

/**
 * Creates a debounced version of a function.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {Function} A new, debounced function.
 */
export const debounce = (func, wait) => {
    let timeout;

    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Custom version of startCase.
 * Replaces lodash version that strips special characters.
 * Also replaces underscores with spaces.
 * 
 */
export const customStartCase = (str) => {
    if (typeof str !== 'string') {
        return str;
    }
    // Convert to lowercase first
    str = str.toLowerCase();
    // Replace underscores with spaces
    str = str.replace(/_/g, ' ');
    // Split the string into words
    return str.split(' ').map(word => {
        // Preserve special characters at the start of the word
        const specialChar = word.match(/^[^a-zA-Z0-9]*/)[0];
        const restOfWord = word.slice(specialChar.length);

        // Capitalize the first letter of the rest of the word
        return specialChar +
            (restOfWord.charAt(0).toUpperCase() +
                // For the rest of the word, only change case if it's all uppercase
                restOfWord.slice(1).replace(/[A-Z]+/g, match => match.toLowerCase()));
    }).join(' ');
};

/**
 * Non-headless retailers - show open or closed hours data based on location timezone
 * 
 */
const stateTimezones = {
    AL: 'America/Chicago',
    AK: 'America/Anchorage',
    AZ: 'America/Phoenix',
    AR: 'America/Chicago',
    CA: 'America/Los_Angeles',
    CO: 'America/Denver',
    CT: 'America/New_York',
    DE: 'America/New_York',
    FL: 'America/New_York',
    GA: 'America/New_York',
    HI: 'Pacific/Honolulu',
    ID: 'America/Boise',
    IL: 'America/Chicago',
    IN: 'America/Indiana/Indianapolis',
    IA: 'America/Chicago',
    KS: 'America/Chicago',
    KY: 'America/New_York',
    LA: 'America/Chicago',
    ME: 'America/New_York',
    MD: 'America/New_York',
    MA: 'America/New_York',
    MI: 'America/Detroit',
    MN: 'America/Chicago',
    MS: 'America/Chicago',
    MO: 'America/Chicago',
    MT: 'America/Denver',
    NE: 'America/Chicago',
    NV: 'America/Los_Angeles',
    NH: 'America/New_York',
    NJ: 'America/New_York',
    NM: 'America/Denver',
    NY: 'America/New_York',
    NC: 'America/New_York',
    ND: 'America/Chicago',
    OH: 'America/New_York',
    OK: 'America/Chicago',
    OR: 'America/Los_Angeles',
    PA: 'America/New_York',
    RI: 'America/New_York',
    SC: 'America/New_York',
    SD: 'America/Chicago',
    TN: 'America/Chicago',
    TX: 'America/Chicago',
    UT: 'America/Denver',
    VT: 'America/New_York',
    VA: 'America/New_York',
    WA: 'America/Los_Angeles',
    WV: 'America/New_York',
    WI: 'America/Chicago',
    WY: 'America/Denver'
};

export function getStoreHoursInfo(currentRetailer, date = new Date()) {
    if (!currentRetailer || !currentRetailer.hours || !currentRetailer.state) {
        return null;
    }

    const timezone = stateTimezones[currentRetailer.state.toUpperCase()] || 'America/New_York';

    // Create a formatter for the day of week
    const dayFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'long' });
    const timeFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', minute: 'numeric', hour12: true });

    // Get the day of week and time in the correct timezone
    const dayOfWeek = dayFormatter.format(date);
    const currentTime = timeFormatter.format(date);

    const todayHours = currentRetailer.hours.find(day => day.day === dayOfWeek);

    if (!todayHours) {
        return null;
    }

    // Parse the current time
    const [currentHour, currentMinute] = currentTime.split(':');
    const currentTotalMinutes = parseInt(currentHour) * 60 + parseInt(currentMinute.split(' ')[0]) + (currentTime.includes('PM') && currentHour !== '12' ? 720 : 0);

    // Parse open and close times
    const [openHour, openMinute] = todayHours.open_time.split(':');
    const [closeHour, closeMinute] = todayHours.close_time.split(':');

    const openTotalMinutes = parseInt(openHour) * 60 + parseInt(openMinute.split(' ')[0]) + (todayHours.open_time.includes('PM') && openHour !== '12' ? 720 : 0);
    let closeTotalMinutes = parseInt(closeHour) * 60 + parseInt(closeMinute.split(' ')[0]) + (todayHours.close_time.includes('PM') && closeHour !== '12' ? 720 : 0);

    if (closeTotalMinutes <= openTotalMinutes) {
        closeTotalMinutes += 1440; // Add 24 hours if closing time is on the next day
    }

    const isCurrentlyOpen = !todayHours.closed && currentTotalMinutes >= openTotalMinutes && currentTotalMinutes < closeTotalMinutes;

    const result = {
        isOpen: isCurrentlyOpen,
        openTime: todayHours.open_time,
        closeTime: todayHours.close_time,
        currentTime: currentTime,
        timezone: timezone,
        isClosed: todayHours.closed
    };

    return result;
}

/**
 * Console debug helper for debug mode.
 */

export const consoleDebug = (message, ...args) => {
    if (getDebugMode()) {
        console.log(message, ...args);
    }
};

/**
 * Checks if the current viewport width is less than 575px
 * @returns {boolean} True if viewport width is less than 575px, false otherwise
 */
export const isMobile = () => {
    // Check if window is defined (important for SSR compatibility)
    if (typeof window === 'undefined') {
        return false;
    }

    return window.innerWidth < 575;
};

export const formatCentsToDollars = (cents) => {
    const roundToTwoDecimals = (num) => Math.round(num * 100) / 100;

    if (cents === null || cents === undefined) return 0;
    return roundToTwoDecimals(cents / 100);
};

/**
 * Determines the highest priority (most recent by start date) special for a given product.
 * @param {string} productId - The ID of the product.
 * @param {Array} allSpecialsDetails - Array of full special objects (e.g., from useSpecialsList).
 * @param {Object} productToSpecialsMapping - Mapping like { specialId: [productId1, productId2], ... }.
 * @returns {Object|null} The full special object that is the highest priority, or null if none.
 */
export const getPrioritySpecialForProduct = (productId, allSpecialsDetails, productToSpecialsMapping) => {
    if (!allSpecialsDetails || !Array.isArray(allSpecialsDetails) || !productToSpecialsMapping || !productId) {
        return null;
    }

    // Check if we have a pre-computed map (from useMenuSpecials hook)
    if (productToSpecialsMapping instanceof Map) {
        const specials = productToSpecialsMapping.get(productId);
        if (!specials || specials.length === 0) return null;

        // Already sorted by priority in the map
        return specials[0];
    }

    // Fallback to original logic for backward compatibility
    const matchingSpecialIds = [];
    for (const specialId in productToSpecialsMapping) {
        if (productToSpecialsMapping[specialId] && productToSpecialsMapping[specialId].includes(productId)) {
            matchingSpecialIds.push(specialId);
        }
    }

    if (matchingSpecialIds.length === 0) {
        return null;
    }

    const matchingSpecials = allSpecialsDetails.filter(special =>
        matchingSpecialIds.includes(special.id)
    );

    if (matchingSpecials.length === 0) {
        return null;
    }

    if (matchingSpecials.length === 1) {
        return matchingSpecials[0];
    }

    // Sort by start date (newest first) - cache the date parsing
    matchingSpecials.sort((a, b) => {
        const aStart = a?._parsedStartDate || (a?.scheduleConfiguration?.startStamp ? new Date(a.scheduleConfiguration.startStamp) : null);
        const bStart = b?._parsedStartDate || (b?.scheduleConfiguration?.startStamp ? new Date(b.scheduleConfiguration.startStamp) : null);

        // Cache parsed dates for reuse
        if (aStart && !a._parsedStartDate) a._parsedStartDate = aStart;
        if (bStart && !b._parsedStartDate) b._parsedStartDate = bStart;

        if (!aStart && !bStart) return 0;
        if (!aStart) return 1;
        if (!bStart) return -1;

        return bStart - aStart;
    });

    return matchingSpecials[0];
};

/**
 * Get Sentry DSN per project based on URL.
 * DSN key in config should match live URL.
 * All dev sites will go to 'range-headless-dev' project.
 * If no matching project found, will go to 'range-headless'
 * @returns {string}
 */
export const getSentryDsn = () => {
    const url = location.hostname.split('.');
    
    if (url.includes('test') || url[1] === 'websitefirstlook') {
        return false;
    }

    let domain = '';
    if (url[0] === 'www') {
        domain = url[1];
    } else {
        domain = url[0];
    }

    if (Object.keys(config.sentryConfigs).includes(domain)) {
        return config.sentryConfigs[domain];
    }

    return config.sentryConfigs['range-headless'];
}
